import { IS_ETS_NEXT } from '@/utils/constants';
import {
  CHROME_CHUNK_IDS,
  CHUNK_DEFINITIONS,
  getChunkDefinition,
  getChunkPath,
  keyMatchesChunk,
  resolveChunkIdForKey,
} from '@/i18n/chunkDefinitions';
import type { I18nChunkId, RouteBundlePlan } from '@/i18n/types';

export {
  CHROME_CHUNK_IDS,
  CHUNK_DEFINITIONS,
  getChunkDefinition,
  getChunkPath,
  keyMatchesChunk,
  resolveChunkIdForKey,
};

const TRAVEL_AUTH_REQUESTS_PREFIX =
  '/travel-management/travel-authorization/requests';
const TRAVEL_POLICY_LIST_PATH =
  '/travel-management/travel-authorization/travel-policy';
// List and create are exact matches; edit uses a prefix so other travel-policy
// paths stay chrome-only.
const TRAVEL_POLICY_CREATE_PATH = `${TRAVEL_POLICY_LIST_PATH}/create`;
const TRAVEL_POLICY_EDIT_PREFIX = `${TRAVEL_POLICY_LIST_PATH}/edit/`;
const AUTHORIZATION_FLOW_LIST_PATH =
  '/travel-management/travel-authorization/authorization-flow';
const AUTHORIZATION_FLOW_CREATE_PATH = `${AUTHORIZATION_FLOW_LIST_PATH}/create`;
const AUTHORIZATION_FLOW_EDIT_PREFIX = `${AUTHORIZATION_FLOW_LIST_PATH}/edit/`;

interface RoutePagePlan {
  routeInScope: boolean;
  pageChunkIds: I18nChunkId[];
  /** Legacy surface ids for scopeKey (cache compatibility). */
  scopeSurfaces: string[];
}

function buildScopeKey(scopeSurfaces: string[]): string {
  if (scopeSurfaces.length === 0) {
    return 'chrome';
  }
  return scopeSurfaces.slice().sort().join('+');
}

function resolveRoutePagePlan(pathname: string): RoutePagePlan {
  if (pathname === '/personal-settings/profile') {
    return {
      routeInScope: true,
      pageChunkIds: ['profilePage', 'sharedCommon'],
      scopeSurfaces: ['profile', 'common'],
    };
  }

  if (pathname === '/hub' && !IS_ETS_NEXT) {
    return {
      routeInScope: true,
      pageChunkIds: ['hubPage', 'hubDialogs', 'sharedCommon'],
      scopeSurfaces: ['hub', 'hubDialogs', 'common'],
    };
  }

  if (pathname.startsWith(TRAVEL_AUTH_REQUESTS_PREFIX)) {
    return {
      routeInScope: true,
      pageChunkIds: ['travelAuthRequests', 'travelAuthShared', 'sharedCommon'],
      scopeSurfaces: ['travelAuth', 'travelAuthShared', 'common'],
    };
  }

  if (
    pathname === TRAVEL_POLICY_LIST_PATH ||
    pathname === TRAVEL_POLICY_CREATE_PATH ||
    pathname.startsWith(TRAVEL_POLICY_EDIT_PREFIX)
  ) {
    return {
      routeInScope: true,
      pageChunkIds: ['travelPolicyPage', 'travelAuthShared', 'sharedCommon'],
      scopeSurfaces: ['travelPolicy', 'travelAuthShared', 'common'],
    };
  }

  if (
    pathname === AUTHORIZATION_FLOW_LIST_PATH ||
    pathname === AUTHORIZATION_FLOW_CREATE_PATH ||
    pathname.startsWith(AUTHORIZATION_FLOW_EDIT_PREFIX)
  ) {
    return {
      routeInScope: true,
      pageChunkIds: [
        'authorizationFlowPage',
        'travelAuthShared',
        'sharedCommon',
      ],
      scopeSurfaces: ['authorizationFlow', 'travelAuthShared', 'common'],
    };
  }

  if (pathname === '/administration/broadcasts') {
    return {
      routeInScope: true,
      pageChunkIds: ['adminBroadcasts', 'sharedCommon'],
      scopeSurfaces: ['administrationBroadcasts', 'common'],
    };
  }

  return {
    routeInScope: false,
    pageChunkIds: [],
    scopeSurfaces: [],
  };
}

export function resolveRouteBundle(pathname: string): RouteBundlePlan {
  const pagePlan = resolveRoutePagePlan(pathname);
  const chunkIds: I18nChunkId[] = [
    ...CHROME_CHUNK_IDS,
    ...pagePlan.pageChunkIds,
  ];

  return {
    routeInScope: pagePlan.routeInScope,
    scopeKey: buildScopeKey(pagePlan.scopeSurfaces),
    chunkIds,
  };
}
